/**
 * Coupon Preview Card Component (Story 7.4)
 *
 * Displays validated coupon details in redemption form.
 */

import React from 'react';
import { Ticket, Calendar, User } from 'lucide-react';
import { useTranslation } from '@/i18n';
import { TimestampService, type ApiTimestamp, type CheckCouponCodeResponseType } from '@zlinkx/shared-v2';

type ValidatedCoupon = NonNullable<CheckCouponCodeResponseType['data']['coupon']>;

interface CouponPreviewCardProps {
	coupon: ValidatedCoupon;
}

const couponTypeBadge = (
	type: string,
	t: (key: string) => string
): { label: string; className: string } => {
	const badges: Record<string, { label: string; className: string }> = {
		percentage: {
			label: t('common.coupon.type.percentage'),
			className: 'bg-blue-100 text-blue-700',
		},
		fixed_amount: {
			label: t('common.coupon.type.fixed'),
			className: 'bg-green-100 text-green-700',
		},
		free_item: {
			label: t('common.coupon.type.freeItem'),
			className: 'bg-purple-100 text-purple-700',
		},
		bogo: {
			label: t('common.coupon.type.bogo'),
			className: 'bg-orange-100 text-orange-700',
		},
	};
	return badges[type] ?? { label: type, className: 'bg-gray-100 text-gray-700' };
};

export const CouponPreviewCard: React.FC<CouponPreviewCardProps> = ({
	coupon,
}) => {
	const { t } = useTranslation();
	const badge = couponTypeBadge(coupon.couponType, t);
	const formatExpiresAt = (expiresAt: ApiTimestamp): string => {
		const parsed = TimestampService.parseDateTimeInput(expiresAt);
		if (!TimestampService.isValidDate(parsed)) {
			return expiresAt;
		}

		const formatted = TimestampService.formatForDisplay(parsed, { style: 'short' });
		const [dateOnly] = formatted.split(',');
		return dateOnly ?? formatted;
	};

	const getDiscountDisplay = (): string => {
		switch (coupon.couponType) {
			case 'percentage':
				return t('common.coupon.percentageOff', {
					value: coupon.discountValue ?? 0,
				});
			case 'fixed_amount':
				// discountValue is in cents, convert to dollars for display
				return t('common.coupon.amountOff', {
					value: ((coupon.discountValue ?? 0) / 100).toFixed(2),
				});
			case 'free_item':
			case 'bogo':
				return coupon.itemDescription ?? coupon.couponType;
			default:
				return '-';
		}
	};

	return (
		<div className='bg-white border border-gray-200 rounded-xl p-4 shadow-sm'>
			<div className='flex items-start gap-4'>
				{/* Icon */}
				<div className='w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0'>
					<Ticket className='w-6 h-6 text-blue-600' />
				</div>

				{/* Content */}
				<div className='flex-1 min-w-0'>
					{/* Title and Badge */}
					<div className='flex items-center gap-2 flex-wrap'>
						<h3 className='font-semibold text-gray-900 truncate'>
							{coupon.title}
						</h3>
						<span
							className={`px-2 py-0.5 text-xs font-medium rounded-full ${badge.className}`}
						>
							{badge.label}
						</span>
					</div>

					{/* Discount Value */}
					<p className='text-lg font-bold text-green-600 mt-1'>
						{getDiscountDisplay()}
					</p>

					{/* Metadata */}
					<div className='flex items-center gap-4 mt-2 text-sm text-gray-500'>
						{coupon.customerName && (
							<div className='flex items-center gap-1'>
								<User className='w-4 h-4' />
								<span>{coupon.customerName}</span>
							</div>
						)}
							{coupon.expiresAt !== null && coupon.expiresAt !== undefined && (
								<div className='flex items-center gap-1'>
									<Calendar className='w-4 h-4' />
									<span>{formatExpiresAt(coupon.expiresAt)}</span>
								</div>
							)}
					</div>

					{/* Minimum Purchase Note */}
					{coupon.minPurchase && coupon.minPurchase > 0 && (
						<p className='text-xs text-gray-500 mt-2'>
							{t('merchant.redemption.minPurchaseNote', {
								amount: coupon.minPurchase,
							})}
						</p>
					)}
				</div>
			</div>
		</div>
	);
};

export default CouponPreviewCard;
